// deployment_class: customer_env_allowed
/**
 * LandingGalaxyScatter — the DIMENSIONAL SCATTER / GALAXY SCHEMA view.
 *
 * Every partnership placed in a dimensional space the viewer CHOOSES: pick the X
 * and Y measures and the portfolio re-forms around them. Bubble area encodes total
 * development cost; colour encodes status; a reference line marks the portfolio
 * average on the Y measure, so "is this deal normal for us?" is answered by looking
 * rather than by arithmetic.
 *
 * The axis pickers are what make it dimensional rather than a fixed chart — cost
 * against units is a different question from cost-per-unit against progress, and a
 * CEO asks both.
 *
 * Every value is derived from each partnership's own record (see landingSchemaViews).
 */
import { useMemo, useState } from 'react';
import {
  GALAXY_AXES,
  SV_STAGE_H,
  SV_STAGE_W,
  galaxyAxis,
  layoutGalaxy,
  type GalaxyPoint,
} from './landingSchemaViews';
import { LandingDetailPopover, type DetailAnchor, type DetailPayload } from './LandingDetailPopover';

type CSSVars = React.CSSProperties & Record<string, string | number>;

export interface GalaxyAxisState {
  x: string;
  y: string;
}

export function LandingGalaxyScatter({
  axes,
  onAxesChange,
}: {
  axes: GalaxyAxisState;
  onAxesChange: (next: GalaxyAxisState) => void;
}): JSX.Element {
  const layout = useMemo(() => layoutGalaxy(axes.x, axes.y), [axes.x, axes.y]);
  const [detail, setDetail] = useState<{ payload: DetailPayload; anchor: DetailAnchor } | null>(
    null,
  );

  const xa = galaxyAxis(axes.x);
  const ya = galaxyAxis(axes.y);
  const { plot } = layout;

  const openPoint = (p: GalaxyPoint, el: HTMLElement): void => {
    const r = el.getBoundingClientRect();
    const m = p.metrics;
    setDetail({
      payload: {
        kindLabel: 'Partnership',
        label: p.name,
        value: `${xa.label} ${p.xValue} · ${ya.label} ${p.yValue}`,
        detail: `${m.structure} with ${m.developer}. $${m.tdc}M across ${m.units} units — $${m.costPerUnit.toLocaleString()} per unit, workflow ${Math.round(m.progress * 100)}% complete. ${m.blurb}`,
        status: p.status,
      },
      anchor: { top: r.top, left: r.left, width: r.width, height: r.height },
    });
  };

  return (
    <>
      {/* Axis pickers — the "dimensional" part. Outside the camera transform so they
          stay legible at any zoom. */}
      <div className="kh-sv-axis-picks" role="group" aria-label="Choose the scatter dimensions">
        <label className="kh-sv-axis-pick">
          <span>X</span>
          <select
            value={axes.x}
            onChange={(e) => onAxesChange({ ...axes, x: e.target.value })}
            aria-label="Horizontal dimension"
          >
            {GALAXY_AXES.map((a) => (
              <option key={a.id} value={a.id}>
                {a.label}
              </option>
            ))}
          </select>
        </label>
        <label className="kh-sv-axis-pick">
          <span>Y</span>
          <select
            value={axes.y}
            onChange={(e) => onAxesChange({ ...axes, y: e.target.value })}
            aria-label="Vertical dimension"
          >
            {GALAXY_AXES.map((a) => (
              <option key={a.id} value={a.id}>
                {a.label}
              </option>
            ))}
          </select>
        </label>
        <span className="kh-sv-axis-note">bubble size = total development cost</span>
      </div>

      <svg
        className="kh-sv-plot"
        viewBox={`0 0 ${SV_STAGE_W} ${SV_STAGE_H}`}
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        {/* Grid */}
        {layout.xTicks.map((t, i) => (
          <line
            key={`gx${i}`}
            className="kh-sv-grid"
            x1={t.pos}
            y1={plot.top}
            x2={t.pos}
            y2={plot.bottom}
          />
        ))}
        {layout.yTicks.map((t, i) => (
          <line
            key={`gy${i}`}
            className="kh-sv-grid"
            x1={plot.left}
            y1={t.pos}
            x2={plot.right}
            y2={t.pos}
          />
        ))}
        {/* Axes */}
        <line
          className="kh-sv-axis"
          x1={plot.left}
          y1={plot.top}
          x2={plot.left}
          y2={plot.bottom}
        />
        <line
          className="kh-sv-axis"
          x1={plot.left}
          y1={plot.bottom}
          x2={plot.right}
          y2={plot.bottom}
        />
        {/* Portfolio-average reference on the Y measure */}
        <line
          className="kh-sv-mean"
          x1={plot.left}
          y1={layout.yMean}
          x2={plot.right}
          y2={layout.yMean}
        />
      </svg>

      {/* Tick labels + axis titles as DOM, so they stay crisp and translatable. */}
      {layout.xTicks.map((t, i) => (
        <span
          key={`tx${i}`}
          className="kh-sv-tick kh-sv-tick-x"
          style={{ left: `${t.pos}px`, top: `${plot.bottom + 14}px` } as CSSVars}
          aria-hidden="true"
        >
          {t.label}
        </span>
      ))}
      {layout.yTicks.map((t, i) => (
        <span
          key={`ty${i}`}
          className="kh-sv-tick kh-sv-tick-y"
          style={{ left: `${plot.left - 14}px`, top: `${t.pos}px` } as CSSVars}
          aria-hidden="true"
        >
          {t.label}
        </span>
      ))}
      <span
        className="kh-sv-axis-title kh-sv-axis-title-x"
        style={{ left: `${(plot.left + plot.right) / 2}px`, top: `${plot.bottom + 54}px` } as CSSVars}
        aria-hidden="true"
      >
        {xa.label}
      </span>
      <span
        className="kh-sv-axis-title kh-sv-axis-title-y"
        style={{ left: `${plot.left - 72}px`, top: `${(plot.top + plot.bottom) / 2}px` } as CSSVars}
        aria-hidden="true"
      >
        {ya.label}
      </span>
      {/* Anchored at the LEFT end of the reference line: the right end is where the
          largest-cost bubble lands, and it ran straight over the label there. */}
      <span
        className="kh-sv-mean-label"
        style={{ left: `${plot.left + 8}px`, top: `${layout.yMean}px` } as CSSVars}
        aria-hidden="true"
      >
        {layout.yMeanLabel}
      </span>

      {layout.points.map((p) => (
        <button
          type="button"
          key={p.id}
          className={`kh-sv-bubble st-${p.status}`}
          style={
            {
              left: `${p.x}px`,
              top: `${p.y}px`,
              '--r': `${p.r}px`,
            } as CSSVars
          }
          onClick={(e) => openPoint(p, e.currentTarget)}
          aria-label={`${p.name}: ${xa.label} ${p.xValue}, ${ya.label} ${p.yValue}, total development cost $${p.metrics.tdc} million`}
        >
          <span className="kh-sv-bubble-name" aria-hidden="true">
            {p.name}
          </span>
        </button>
      ))}

      {detail && (
        <LandingDetailPopover
          payload={detail.payload}
          anchor={detail.anchor}
          onClose={() => setDetail(null)}
        />
      )}
    </>
  );
}
